<template>
  <BurnLiquidityPreview
    v-if="view === 'root'"
    @close="onClose"
    @confirm="onBurnConfirm"
  ></BurnLiquidityPreview>
  <SendTransaction
    v-if="view === 'tx'"
    :payload="payload"
    :stable="isCurveStabe"
    :title="$t('burn.removeLiquidity')"
    flow="burn"
    @back="onBack"
    @close="onClose"
    @reject="onRejected"
    @success="onSuccess"
  >
    <template #tx-params>
      <BurnLiquidityInfo />
      <PInlineMessage v-if="ratesHasChanged" class="mt-3" severity="warn">
        {{ $t('errors.rateHasChanged') }}
      </PInlineMessage>
    </template>
  </SendTransaction>
</template>

<script lang="ts" setup>
import { computed, inject, ref, unref, watch } from 'vue';
import { SendTransaction } from '@/components/Dialogs/Steps';
import BurnLiquidityPreview from './BurnLiquidityPreview.vue';
import BurnLiquidityInfo from './BurnLiquidityInfo.vue';
import { TxPayloadCallFunction } from '@/types/aptosResources';
import {
  composeType,
  getCurve,
  getModulesAccount,
  getScriptsFor,
  is_sorted,
} from '@/utils/contracts';
import { useBurnLiquidityStore, useDialogStore } from '@/store';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { useTimeoutPoll } from '@vueuse/core';
import {
  MODULES_V1_ACCOUNT,
  MULTI_ROUTER_DEFAULT,
  RECALCULATION_TIME,
  VERSION_SOLO,
} from '@/constants';
import { EMixpanelEventNames, EMixpanelProvideKeys } from '@/types/enums';
import { useTokensStore } from '@/store';

const track: any = inject(EMixpanelProvideKeys.Track);

const emits = defineEmits(['success', 'reject', 'back', 'close']);

const burnStore = useBurnLiquidityStore();
const version = computed(() => burnStore.version);
const dialogStore = useDialogStore();
const pool = computed(() => burnStore.pool);
const tokensStore = useTokensStore();

// const poolsStore = usePoolsStore();

const view = ref<'root' | 'tx'>('root');
const ratesHasChanged = ref(false);
const isSolo = ref(version.value === VERSION_SOLO);
const cachedPayload = ref<TxPayloadCallFunction>();
const payload = computed<TxPayloadCallFunction>(() => {
  const address = MULTI_ROUTER_DEFAULT;
  const module = 'router';

  const isBurnWithFa =
    tokensStore.getToken(burnStore.fromCurrency.token ?? '')?.isFungibleAsset ??
    tokensStore.getToken(burnStore.toCurrency.token ?? '')?.isFungibleAsset;
  const moduleAddress = getModulesAccount(version.value);
  const scriptsVersion = getScriptsFor(version.value);
  const functionName = isBurnWithFa
    ? composeType(
        address,
        module,
        `remove_liquidity_v${version.value}`.replace('.', ''),
      )
    : composeType(
        moduleAddress,
        scriptsVersion,
        isSolo.value ? 'withdraw' : 'remove_liquidity',
      );

  const { isSorted } = burnStore;
  const isSortedForSolo = is_sorted(
    burnStore.fromCurrency.token ?? '',
    burnStore.toCurrency.token ?? '',
  );

  const burnAmount = (burnStore.burnAmount as number).toString();
  const xOutput = (burnStore.output as any)?.x.toFixed(0) ?? '0';
  const yOutput = (burnStore.output as any)?.y.toFixed(0) ?? '0';
  const soloPool = pool.value;

  const args = isSolo.value
    ? [soloPool?.vault, Number(burnStore.burnAmount?.toFixed(0))]
    : isSorted
    ? [burnAmount, xOutput, yOutput]
    : [burnAmount, yOutput, xOutput];

  const typeArguments = isSolo.value
    ? isSortedForSolo
      ? [
          burnStore.fromCurrency.token,
          burnStore.toCurrency.token,
          composeType(MODULES_V1_ACCOUNT, 'bin_steps', `X${soloPool?.binStep}`),
        ]
      : [
          burnStore.toCurrency.token,
          burnStore.fromCurrency.token,
          composeType(MODULES_V1_ACCOUNT, 'bin_steps', `X${soloPool?.binStep}`),
        ]
    : isSorted
    ? [
        burnStore.fromCurrency.token,
        burnStore.toCurrency.token,
        burnStore.curve,
      ]
    : [
        burnStore.toCurrency.token,
        burnStore.fromCurrency.token,
        burnStore.curve,
      ];

  return {
    type: 'entry_function_payload',
    function: functionName,
    typeArguments: typeArguments,
    functionArguments: args,
  } as TxPayloadCallFunction;
});
const dialogRef = inject('dialogRef');

const isCurveStabe = computed(() =>
  version.value === VERSION_SOLO
    ? false
    : burnStore.curve === getCurve('stable', version.value),
);

watch(
  [payload, cachedPayload],
  () => {
    if (!payload.value || !cachedPayload.value) {
      ratesHasChanged.value = false;
      return;
    }

    ratesHasChanged.value = !isEqual(payload.value, cachedPayload.value);
  },
  { deep: true },
);

useTimeoutPoll(() => burnStore.refetchRates(true), RECALCULATION_TIME, {
  immediate: true,
});

function onBurnConfirm() {
  track(EMixpanelEventNames.RemoveLiquidityConfirmClicked);

  cachedPayload.value = cloneDeep(unref(payload.value));
  view.value = 'tx';
  dialogStore.setHasBack(true);
  dialogStore.constructedBack(() => {
    view.value = 'root';
    dialogStore.setHasBack(false);
  });
}

function onBack() {
  view.value = 'root';
  cachedPayload.value = undefined;
}

function onClose() {
  onBack();
  emits('close');
  dialogRef.value.close();
}

function onSuccess() {
  emits('success');
  dialogRef.value.close({ success: true });
}

function onRejected() {
  emits('reject');
}
</script>
